import { useQuery } from '@tanstack/react-query';
import { normalizeAccountConfig, type AccountConfig } from '@course-authoring/shared-types';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const accountConfigKeys = {
  all: ['account-config'] as const,
  scope: (accountId: number | null) =>
    [...accountConfigKeys.all, 'scope', accountId ?? 'base'] as const,
};

/**
 * Fetches an account's config from author-api. The endpoint requires a positive
 * integer account id (there is no base/null variant, unlike account-theme), so
 * callers must only invoke this for a resolved client account.
 */
export async function fetchAccountConfig(
  accountId: number,
  signal?: AbortSignal,
): Promise<AccountConfig> {
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/scorm-accounts/${accountId}/config`,
    { signal },
    { json: 'withBody' },
  );
  // Coerce at the boundary so the react-query cache only ever holds a complete
  // config: hoot can omit fields (e.g. selectedLanguages for an account with no
  // language row) and any consumer indexing the raw payload would crash.
  const raw = await handleApiResponse<Partial<AccountConfig>>(
    response,
    'Failed to load account config',
  );
  return normalizeAccountConfig(raw);
}

export function useAccountConfigQuery(
  accountId: number | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: accountConfigKeys.scope(accountId),
    queryFn: ({ signal }) => fetchAccountConfig(accountId as number, signal),
    enabled: options?.enabled !== false && accountId !== null,
    // Override the queryClient's global keep-previous-data placeholder: on a
    // client-context switch the prior account's config must never be served
    // for the new scope (a disabled base-scope query would show it forever).
    placeholderData: undefined,
  });
}
